import * as XLSX from "xlsx";
import { MaterialStockRow, ValidationError, ValidationResult } from "./types";

const REQUIRED_HEADERS = ["material_code", "name", "storage", "category", "measurement_unit", "quantity"];
const ALLOWED_STORAGES = ["Kho 1", "Kho 2"];

const toNumber = (v: any): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return v;
    const s = String(v).trim();
    if (!s) return null;
    const normalized = s.replace(/,/g, ".").replace(/[^\d.\-]/g, "");
    const n = parseFloat(normalized);
    return isNaN(n) ? null : n;
};

export const validateExcelFile = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                
                // Always use the first sheet (sheet[0]) - Excel files may have multiple sheets
                const sheetName = workbook.SheetNames?.[0];
                if (!sheetName || !workbook.Sheets[sheetName]) {
                    reject(new Error("Không tìm thấy sheet đầu tiên"));
                    return;
                }

                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

                if (jsonData.length < 3) {
                    reject(new Error("File phải có ít nhất 3 dòng (headers ở dòng 3)"));
                    return;
                }

                const headerRow = jsonData[2];
                if (!headerRow || headerRow.length === 0) {
                    reject(new Error("Không tìm thấy headers ở dòng 3"));
                    return;
                }

                // Header mapping (case-insensitive, supports Vietnamese)
                const headerMapping: { [key: string]: string } = {
                    "material_code": "material_code",
                    "mã vật tư": "material_code",
                    "ma vat tu": "material_code",
                    "name": "name",
                    "tên vật tư": "name",
                    "ten vat tu": "name",
                    "storage": "storage",
                    "kho số": "storage",
                    "kho so": "storage",
                    "kho": "storage",
                    "category": "category",
                    "hạng mục": "category",
                    "hang muc": "category",
                    "measurement_unit": "measurement_unit",
                    "đơn vị": "measurement_unit",
                    "don vi": "measurement_unit",
                    "quantity": "quantity",
                    "số lượng": "quantity",
                    "so luong": "quantity",
                    "tồn kho": "quantity",
                    "ton kho": "quantity",
                    "TỒN Kho": "quantity",  // Add uppercase variant
                    "TỒN KHO": "quantity",
                    "Tồn Kho": "quantity",
                };

                const fieldMap: { [key: string]: number } = {};
                headerRow.forEach((h: any, colIdx: number) => {
                    if (!h) return;
                    const rawKey = String(h).trim();
                    const normalizedKey = rawKey.toLowerCase();
                    
                    // Try exact match first (case-sensitive)
                    let mappedField = headerMapping[rawKey];
                    if (!mappedField) {
                        // Try normalized (case-insensitive)
                        mappedField = headerMapping[normalizedKey];
                    }
                    // Also check if it's exactly one of the required headers (case-insensitive)
                    if (!mappedField) {
                        const exactMatch = REQUIRED_HEADERS.find(req => req.toLowerCase() === normalizedKey);
                        if (exactMatch) {
                            mappedField = exactMatch;
                        }
                    }
                    
                    if (mappedField) {
                        fieldMap[mappedField] = colIdx;
                    }
                });

                const missingHeaders = REQUIRED_HEADERS.filter((h) => fieldMap[h] === undefined);
                if (missingHeaders.length > 0) {
                    
                    // Check if "quantity" is missing but "threshold" exists (common mistake)
                    const hasThreshold = headerRow.some((h: any) => h && String(h).toLowerCase().includes("threshold"));
                    const hasQuantity = headerRow.some((h: any) => h && String(h).toLowerCase().includes("quantity"));
                    
                    let errorMsg = `Thiếu headers bắt buộc ở dòng 3: ${missingHeaders.join(", ")}.\n\n`;
                    errorMsg += `=== NỘI DUNG FILE EXCEL ===\n`;
                    errorMsg += `Dòng 1: ${jsonData[0]?.map((h: any) => `"${h}"`).join(", ") || "(trống)"}\n`;
                    errorMsg += `Dòng 2: ${jsonData[1]?.map((h: any) => `"${h}"`).join(", ") || "(trống)"}\n`;
                    errorMsg += `Dòng 3: ${headerRow.map((h: any) => `"${h}"`).join(", ")}\n\n`;
                    errorMsg += `Headers tìm thấy ở dòng 3: ${headerRow.map((h: any) => `"${h}"`).join(", ")}\n\n`;
                    
                    if (missingHeaders.includes("quantity") && hasThreshold && !hasQuantity) {
                        errorMsg += `⚠️ LƯU Ý: Bạn có header "threshold" ở cột ${headerRow.findIndex((h: any) => h && String(h).toLowerCase().includes("threshold")) + 1} nhưng thiếu "quantity". `;
                        errorMsg += `Vui lòng kiểm tra lại dòng 3 trong file Excel - cột số lượng phải có header là "quantity" (không phải "threshold").\n\n`;
                        errorMsg += `Các header bắt buộc ở dòng 3: ${REQUIRED_HEADERS.join(", ")}`;
                    } else {
                        errorMsg += `Các header bắt buộc ở dòng 3: ${REQUIRED_HEADERS.join(", ")}`;
                    }
                    
                    reject(new Error(errorMsg));
                    return;
                }

                const rows: MaterialStockRow[] = [];
                const errors: ValidationError[] = [];
                let originalRowCount = 0;

                for (let rowIdx = 3; rowIdx < jsonData.length; rowIdx++) {
                    const row = jsonData[rowIdx];
                    const rowNumber = rowIdx + 1;

                    if (!row || row.every((cell: any) => cell === null || cell === undefined || String(cell).trim() === "")) {
                        continue;
                    }

                    originalRowCount++;
                    const rowErrors: ValidationError[] = [];
                    const data: any = {};

                    for (const [field, colIdx] of Object.entries(fieldMap)) {
                        const raw = row[colIdx];
                        if (field === "quantity") {
                            const n = toNumber(raw);
                            if (n === null) data.quantity = null;
                            else data.quantity = n;
                        } else {
                            data[field] = raw === null || raw === undefined ? "" : String(raw).trim();
                        }
                    }

                    if (!data.material_code) {
                        rowErrors.push({ row: rowNumber, field: "material_code", error: "Mã vật tư là bắt buộc" });
                    }

                    if (!data.storage) {
                        rowErrors.push({ row: rowNumber, field: "storage", error: "Kho là bắt buộc" });
                    } else if (!ALLOWED_STORAGES.includes(data.storage)) {
                        rowErrors.push({
                            row: rowNumber,
                            field: "storage",
                            error: `Kho không hợp lệ: ${data.storage}. Chỉ cho phép Kho 1 hoặc Kho 2`,
                        });
                    }

                    if (data.quantity === null || typeof data.quantity !== "number" || data.quantity < 0) {
                        rowErrors.push({ row: rowNumber, field: "quantity", error: "Số lượng phải là số >= 0" });
                    }

                    const rowToAdd: MaterialStockRow = {
                        rowNumber,
                        data,
                        isValid: rowErrors.length === 0,
                        errors: rowErrors,
                    };

                    rows.push(rowToAdd);
                    errors.push(...rowErrors);
                }

                resolve({ rows, errors, originalRowCount });
            } catch (err: any) {
                reject(new Error(`Lỗi đọc file: ${err?.message || String(err)}`));
            }
        };
        reader.onerror = () => reject(new Error("Không thể đọc file"));
        reader.readAsArrayBuffer(file);
    });
};

export const mergePrecheckErrors = (
    rows: MaterialStockRow[],
    precheckErrors: Array<{ row: number; field?: string; error: string }>
): { rows: MaterialStockRow[]; errors: ValidationError[] } => {
    const byRow: { [row: number]: ValidationError[] } = {};
    precheckErrors.forEach((e) => {
        const row = e.row;
        if (!byRow[row]) byRow[row] = [];
        byRow[row].push({ row, field: e.field, error: e.error });
    });

    const mergedRows = rows.map((r) => {
        const extra = byRow[r.rowNumber] || [];
        const allErrors = [...r.errors, ...extra];
        return {
            ...r,
            errors: allErrors,
            isValid: allErrors.length === 0,
        };
    });

    const mergedErrors: ValidationError[] = [];
    mergedRows.forEach((r) => mergedErrors.push(...r.errors));

    return { rows: mergedRows, errors: mergedErrors };
};


